import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import ListerProductsTableComponent from "../../components/tables/Lister/ListerProductsTableComponent";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";

export default function ListerProductListPage() {
  const { t, n } = useContext(TranslatorContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("new");

  const [allProducts, setAllProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);

  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [storesOptions, setStoresOptions] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedStore, setSelectedStore] = useState("");

  const [counts, setCounts] = useState({
    new: 0,
    listed: 0,
    checker_assigned: 0,
    approved: 0,
    rejected: 0,
  });

  const tableHeaders = [
    "Product Code",
    "Title",
    "Category",
    "Brand",
    "Selling Style",
    "Seller Price",
    "Quantity",
    "Action",
  ];

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    },
  });

  const extractData = (res) => {
    if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  };

  const fetchInitialFilters = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${baseUrl}/lister/initial-data`, getAuthHeader());
      const resData = response.data?.data || response.data;

      if (resData?.product_categories) {
        const flattenedCategories = [];
        resData.product_categories.forEach((cat) => {
          if (cat.child_categories && cat.child_categories.length > 0) {
            cat.child_categories.forEach((child) => {
              flattenedCategories.push({
                id: child.id,
                name: `${cat.name} > ${child.name}`,
              });
            });
          } else {
            flattenedCategories.push({
              id: cat.id,
              name: cat.name,
            });
          }
        });
        setCategoriesOptions(flattenedCategories);
      }

      if (resData?.brands) setBrandsOptions(resData.brands);
      if (resData?.stores) setStoresOptions(resData.stores);
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const [newRes, listedRes, assignedRes, approvedRes, rejectedRes] = await Promise.all([
        axios.get(`${baseUrl}/lister/products`, getAuthHeader()),
        axios.get(`${baseUrl}/lister/listed-products`, getAuthHeader()),
        axios.get(`${baseUrl}/lister/checker-assigned-products`, getAuthHeader()),
        axios.get(`${baseUrl}/lister/approved-products`, getAuthHeader()),
        axios.get(`${baseUrl}/lister/reject-products`, getAuthHeader()),
      ]);

      setCounts({
        new: extractData(newRes).length,
        listed: extractData(listedRes).length,
        checker_assigned: extractData(assignedRes).length,
        approved: extractData(approvedRes).length,
        rejected: extractData(rejectedRes).length,
      });
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  }, []);

  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);

    let endpoint = "";
    if (tab === "new") endpoint = "/lister/products";
    else if (tab === "listed") endpoint = "/lister/listed-products";
    else if (tab === "assigned") endpoint = "/lister/checker-assigned-products";
    else if (tab === "approved") endpoint = "/lister/approved-products";
    else if (tab === "rejected") endpoint = "/lister/reject-products";

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        getAuthHeader()
      );

      const listData = extractData(response);
      setAllProducts(listData);
      setProductsList(listData);
    } catch (err) {
      console.error("Error fetching tab data:", err);
      setError(
        err.response?.data?.message || "Failed to fetch products. Please try again."
      );
      setAllProducts([]);
      setProductsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (tabParam && ["new", "listed", "assigned", "approved", "rejected"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    fetchInitialFilters();
    fetchCounts();
  }, [fetchInitialFilters, fetchCounts]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  useEffect(() => {
    let filtered = [...allProducts];

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (p) =>
          (p.title && p.title.toLowerCase().includes(query)) ||
          (p.product_code && p.product_code.toLowerCase().includes(query))
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (p) => String(p.category_id || p.category?.id) === String(selectedCategory)
      );
    }

    if (selectedBrand) {
      filtered = filtered.filter(
        (p) => String(p.brand_id || p.brand?.id) === String(selectedBrand)
      );
    }

    if (selectedStore) {
      filtered = filtered.filter(
        (p) => String(p.store_id || p.store?.id) === String(selectedStore)
      );
    }

    setProductsList(filtered);
  }, [allProducts, searchTerm, selectedCategory, selectedBrand, selectedStore]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    navigate(`/lister-products?tab=${tabKey}`);
  };

  const cardsData = [
    {
      key: "new",
      title: "New Products",
      digit: counts.new,
      icon: "shopping_bag",
      variant: activeTab === "new" ? "lg blue active-card" : "lg blue",
    },
    {
      key: "listed",
      title: "Listed Products",
      digit: counts.listed,
      icon: "widgets",
      variant: activeTab === "listed" ? "lg purple active-card" : "lg purple",
    },
    {
      key: "assigned",
      title: "Checker Assigned",
      digit: counts.checker_assigned,
      icon: "assignment_ind",
      variant: activeTab === "assigned" ? "lg yellow active-card" : "lg yellow",
    },
    {
      key: "approved",
      title: "Approved Products",
      digit: counts.approved,
      icon: "verified_user",
      variant: activeTab === "approved" ? "lg green active-card" : "lg green",
    },
    {
      key: "rejected",
      title: "Rejected Products",
      digit: counts.rejected,
      icon: "cancel",
      variant: activeTab === "rejected" ? "lg red active-card" : "lg red",
    },
  ];

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                {t("product_list")} ({productsList.length})
              </h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("products")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{t("product_list")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {cardsData.map((card) => (
          <Col key={card.key} xs={12} sm={6} md={2} className="mb-3">
            <div
              style={{ cursor: "pointer" }}
              onClick={() => handleTabChange(card.key)}
            >
              <FloatCardComponent
                variant={card.variant}
                digit={n ? n(card.digit) : card.digit}
                title={card.title}
                icon={card.icon}
              />
            </div>
          </Col>
        ))}

        <Col xl={12}>
          <div className="mc-card">
            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            <Row className="mb-4">
              <Col xs={12} sm={6} md={3}>
                <LabelFieldComponent
                  type="search"
                  placeholder={t("search_by_title_or_code")}
                  label={t("search")}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-3"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>

              <Col xs={12} sm={6} md={3}>
                <LabelFieldComponent
                  label={t("category_by")}
                  option={[
                    "All Categories",
                    ...categoriesOptions.map((c) => c.name),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-3"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "All Categories") {
                      setSelectedCategory("");
                      return;
                    }
                    const selected = categoriesOptions.find((c) => c.name === value);
                    setSelectedCategory(selected?.id || "");
                  }}
                />
              </Col>

              <Col xs={12} sm={6} md={3}>
                <LabelFieldComponent
                  label={t("brand_by")}
                  option={[
                    "All Brands",
                    ...brandsOptions.map((b) => b.name || b.title),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-3"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "All Brands") {
                      setSelectedBrand("");
                      return;
                    }
                    const selected = brandsOptions.find((b) => (b.name || b.title) === value);
                    setSelectedBrand(selected?.id || "");
                  }}
                />
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_products...")}</p>
                  </div>
                ) : (
                  <ListerProductsTableComponent
                    thead={tableHeaders}
                    tbody={productsList}
                    activeTab={activeTab}
                  />
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}